import React, { useState, useEffect } from "react";
import api from "../../utils/api";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Button,
  Checkbox,
  FormControlLabel,
  Box,
  Grid,
  FormHelperText,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";

export default function UpdateModal({ open, handleClose, onSuccess, vehicleId }) {
  const [formData, setFormData] = useState({
    modele: "",
    nombrePlaces: "",
    type: "",
    typeMoteur: "",
    etatVehicule: "",
    dateMiseEnCirculation: "",
    kmDepart: "",
    plaqueImmatriculation: "",
    dateAcquisition: "",
    enService: false,
    chauffeur: "",
    logo: null,
    logoPreview: null,
  });
  
  // État pour suivre si l'image a échoué à charger
  const [imageLoadError, setImageLoadError] = useState(false);
  
  const [vehicleModels, setVehicleModels] = useState([]);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [loading, setLoading] = useState({
    models: true,
    chauffeurs: true,
    vehicle: vehicleId ? true : false
  });
  const [error, setError] = useState({
    models: null,
    chauffeurs: null,
    vehicle: null
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  
  // Fetch vehicle models and chauffeurs when component mounts
  useEffect(() => {
    fetchVehicleModels();
    fetchChauffeurs();
    if (vehicleId) {
      fetchVehicleData();
    }
    // Réinitialiser l'erreur de chargement d'image lorsque vehicleId change
    setImageLoadError(false);
    
    // Fonction de nettoyage pour libérer les URL d'objets
    return () => {
      // Libérer l'URL de l'objet si elle existe
      if (formData.logoPreview && formData.logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(formData.logoPreview);
      }
    };
  }, [vehicleId]);
  
  const fetchVehicleData = async () => {
    if (!vehicleId) return;
    
    setLoading(prev => ({ ...prev, vehicle: true }));
    setError(prev => ({ ...prev, vehicle: null }));
    
    try {
      const response = await api.get(`/vehicles/${vehicleId}`);
      const vehicle = response.data;
      
      // Récupérer les données du véhicule
      setFormData({
        modele: vehicle.ID_Model || "",
        nombrePlaces: vehicle.seats || "",
        type: vehicle.type || "",
        typeMoteur: vehicle.type_moteur || "",
        etatVehicule: vehicle.etat || "",
        dateMiseEnCirculation: vehicle.date_circulation || "",
        kmDepart: vehicle.kilometrage || "",
        plaqueImmatriculation: vehicle.immatriculation || "",
        dateAcquisition: vehicle.date_exp_inscription || "",
        enService: vehicle.en_service ? true : false,
        chauffeur: vehicle.ID_User || "",
        logo: null,
        logoPreview: null, // Initialiser à null, sera mis à jour après la récupération de l'image
      });
      
      // Réinitialiser l'erreur de chargement d'image
      setImageLoadError(false);
      
      // Récupérer l'image du véhicule avec l'instance API pour inclure l'authentification
      if (vehicleId) {
        try {
          const imageResponse = await api.get(`/vehicles/${vehicleId}/image`, {
            responseType: 'blob'
          });
          
          // Créer une URL d'objet à partir du blob
          const imageUrl = URL.createObjectURL(imageResponse.data);
          
          // Mettre à jour le state avec l'URL de l'image
          setFormData(prev => ({
            ...prev,
            logoPreview: imageUrl
          }));
        } catch (imageErr) {
          console.error('Error fetching vehicle image:', imageErr);
          setImageLoadError(true);
        }
      }
    } catch (err) {
      console.error('Error fetching vehicle data:', err);
      setError(prev => ({ ...prev, vehicle: 'Failed to load vehicle data' }));
    } finally {
      setLoading(prev => ({ ...prev, vehicle: false }));
    }
  };
  
  const fetchVehicleModels = async () => {
    setLoading(prev => ({ ...prev, models: true }));
    setError(prev => ({ ...prev, models: null }));
    try {
      const response = await api.get('/vehicle-models');
      setVehicleModels(response.data);
    } catch (err) {
      console.error('Error fetching vehicle models:', err);
      setError(prev => ({ ...prev, models: 'Failed to load vehicle models' }));
    } finally {
      setLoading(prev => ({ ...prev, models: false }));
    }
  };

  const fetchChauffeurs = async () => {
    setLoading(prev => ({ ...prev, chauffeurs: true }));
    setError(prev => ({ ...prev, chauffeurs: null }));
    try {
      const response = await api.get('/roles/3');
      if (response.data && response.data.users) {
        setChauffeurs(response.data.users);
      }
    } catch (err) {
      console.error('Error fetching chauffeurs:', err);
      setError(prev => ({ ...prev, chauffeurs: 'Failed to load chauffeurs' }));
    } finally {
      setLoading(prev => ({ ...prev, chauffeurs: false }));
    }
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
    
    // Clear error for this field when user makes changes
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  const resetForm = () => {
    setErrors({});
    setGeneralError("");
    setSubmitError(null);
  };
  
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    // Validation basée sur les règles du VehicleController.php
    // Champs obligatoires: type, type_moteur, etat, kilometrage, immatriculation
    if (!formData.type) {
      newErrors.type = "Le type de véhicule est requis";
      isValid = false;
    }
    
    if (!formData.typeMoteur) {
      newErrors.typeMoteur = "Le type de moteur est requis";
      isValid = false;
    }
    
    if (!formData.etatVehicule) {
      newErrors.etatVehicule = "L'état du véhicule est requis";
      isValid = false;
    }
    
    if (!formData.kmDepart) {
      newErrors.kmDepart = "Le kilométrage est requis";
      isValid = false;
    } else if (isNaN(formData.kmDepart)) {
      newErrors.kmDepart = "Le kilométrage doit être un nombre";
      isValid = false;
    }
    
    if (!formData.plaqueImmatriculation) {
      newErrors.plaqueImmatriculation = "L'immatriculation est requise";
      isValid = false;
    }
    
    // Validation des champs numériques facultatifs
    if (formData.nombrePlaces && isNaN(formData.nombrePlaces)) {
      newErrors.nombrePlaces = "Le nombre de places doit être un nombre entier";
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };
  
  const handleSubmit = async () => {
    if (!vehicleId) return;
    
    setSubmitting(true);
    setSubmitError(null);
    setGeneralError("");
    setErrors({});
    
    // Validation du formulaire
    if (!validateForm()) {
      setSubmitting(false);
      return;
    }
    
    try {
      // Create FormData object for file upload
      const vehicleData = new FormData();
      
      // Add all form fields to FormData
      if (formData.modele) vehicleData.append('ID_Model', formData.modele);
      if (formData.chauffeur) vehicleData.append('ID_User', formData.chauffeur);
      if (formData.nombrePlaces) vehicleData.append('seats', formData.nombrePlaces);
      if (formData.type) vehicleData.append('type', formData.type);
      if (formData.typeMoteur) vehicleData.append('type_moteur', formData.typeMoteur);
      if (formData.etatVehicule) vehicleData.append('etat', formData.etatVehicule);
      if (formData.dateMiseEnCirculation) vehicleData.append('date_circulation', formData.dateMiseEnCirculation);
      if (formData.kmDepart) vehicleData.append('kilometrage', formData.kmDepart);
      if (formData.plaqueImmatriculation) vehicleData.append('immatriculation', formData.plaqueImmatriculation);
      if (formData.dateAcquisition) vehicleData.append('date_exp_inscription', formData.dateAcquisition);
      vehicleData.append('en_service', formData.enService ? 1 : 0);
      
      // Add logo if exists
      if (formData.logo) {
        vehicleData.append('images', formData.logo);
      }
      
      // Use POST method for update
      const response = await api.post(`/vehicles/${vehicleId}`, vehicleData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Handle success
      if (response.status === 200) {
        resetForm();
        if (onSuccess) onSuccess(response.data);
        handleClose();
      }
    } catch (err) {
      console.error('Error updating vehicle:', err);
      
      // Traitement des erreurs de validation (422)
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        const fieldErrors = {};
        
        // Mapper les erreurs du backend aux champs du formulaire
        Object.keys(validationErrors).forEach(key => {
          switch(key) {
            case 'ID_Model':
              fieldErrors.modele = validationErrors[key][0];
              break;
            case 'ID_User':
              fieldErrors.chauffeur = validationErrors[key][0];
              break;
            case 'type':
              fieldErrors.type = validationErrors[key][0];
              break;
            case 'type_moteur':
              fieldErrors.typeMoteur = validationErrors[key][0];
              break;
            case 'etat':
              fieldErrors.etatVehicule = validationErrors[key][0];
              break;
            case 'kilometrage':
              fieldErrors.kmDepart = validationErrors[key][0];
              break;
            case 'immatriculation':
              fieldErrors.plaqueImmatriculation = validationErrors[key][0];
              break;
            case 'date_circulation':
              fieldErrors.dateMiseEnCirculation = validationErrors[key][0];
              break;
            case 'date_exp_inscription':
              fieldErrors.dateAcquisition = validationErrors[key][0];
              break;
            case 'seats':
              fieldErrors.nombrePlaces = validationErrors[key][0];
              break;
            case 'images':
              fieldErrors.logo = validationErrors[key][0];
              break;
            default:
              // Pour les erreurs qui ne correspondent pas à un champ spécifique
              if (!fieldErrors._general) fieldErrors._general = [];
              fieldErrors._general.push(validationErrors[key][0]);
          }
        });
        
        setErrors(fieldErrors);
        if (fieldErrors._general) {
          setGeneralError(fieldErrors._general.join(' '));
        }
      } 
      // Traitement des erreurs de validation (400)
      else if (err.response?.status === 400) {
        const validationErrors = err.response.data || {};
        const fieldErrors = {};
        
        // Mapper les erreurs du backend aux champs du formulaire
        Object.keys(validationErrors).forEach(key => {
          switch(key) {
            case 'ID_Model':
              fieldErrors.modele = validationErrors[key][0];
              break;
            case 'ID_User':
              fieldErrors.chauffeur = validationErrors[key][0];
              break;
            case 'type':
              fieldErrors.type = validationErrors[key][0];
              break;
            case 'type_moteur':
              fieldErrors.typeMoteur = validationErrors[key][0];
              break;
            case 'etat':
              fieldErrors.etatVehicule = validationErrors[key][0];
              break;
            case 'kilometrage':
              fieldErrors.kmDepart = validationErrors[key][0];
              break;
            case 'immatriculation':
              fieldErrors.plaqueImmatriculation = validationErrors[key][0];
              break;
            case 'date_circulation':
              fieldErrors.dateMiseEnCirculation = validationErrors[key][0];
              break;
            case 'date_exp_inscription':
              fieldErrors.dateAcquisition = validationErrors[key][0];
              break;
            case 'seats':
              fieldErrors.nombrePlaces = validationErrors[key][0];
              break;
            case 'images':
              fieldErrors.logo = validationErrors[key][0];
              break;
            default:
              // Pour les erreurs qui ne correspondent pas à un champ spécifique
              if (!fieldErrors._general) fieldErrors._general = [];
              fieldErrors._general.push(validationErrors[key][0]);
          }
        });
        
        setErrors(fieldErrors);
        if (fieldErrors._general) {
          setGeneralError(fieldErrors._general.join(' '));
        }
      }
      // Erreur serveur (500)
      else if (err.response?.status === 500) {
        setGeneralError(err.response.data?.message || "Une erreur serveur s'est produite. Veuillez réessayer plus tard.");
      }
      // Erreur réseau ou autre
      else {
        setGeneralError("Erreur de connexion. Veuillez vérifier votre connexion internet et réessayer.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Libérer l'ancienne URL d'objet si elle existe
      if (formData.logoPreview && formData.logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(formData.logoPreview);
      }
      
      // Créer une nouvelle URL d'objet pour le fichier sélectionné
      const newLogoPreview = URL.createObjectURL(file);
      
      setFormData((prevData) => ({
        ...prevData,
        logo: file,
        logoPreview: newLogoPreview
      }));
      
      // Réinitialiser l'erreur de chargement d'image lorsqu'une nouvelle image est sélectionnée
      setImageLoadError(false);
    }
  };

  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      height: 35,
      '&:hover fieldset': { borderColor: '#000' },
      '&.Mui-focused fieldset': { borderColor: 'red' }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 400, mr: 3 }}>
        MODIFIER UN VÉHICULE
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generalError}
          </Alert>
        )}
         <Box sx={{ backgroundColor: '#000', p: 1 }}>
                  <Typography variant="subtitle1" color="white">
                  INFORMATIONS GÉNÉRALES
                  </Typography>
                </Box>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Modèle</Typography>
            <FormControl fullWidth sx={inputStyle} error={!!errors.modele}>
              <Select 
                name="modele" 
                value={formData.modele} 
                onChange={handleChange}
                disabled={loading.models}
              >
                {loading.models ? (
                  <MenuItem value="">Chargement...</MenuItem>
                ) : error.models ? (
                  <MenuItem value="">Erreur de chargement</MenuItem>
                ) : (
                  vehicleModels.map((model) => (
                    <MenuItem key={model.ID_Model} value={model.ID_Model}>
                      {model.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.modele && <FormHelperText error>{errors.modele}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Nombre de places</Typography>
            <TextField 
              fullWidth 
              name="nombrePlaces" 
              value={formData.nombrePlaces} 
              onChange={handleChange} 
              variant="outlined" 
              sx={inputStyle} 
              error={!!errors.nombrePlaces}
              helperText={errors.nombrePlaces}
              type="number"
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Type</Typography>
            <FormControl fullWidth sx={inputStyle} error={!!errors.type}>
              <Select name="type" value={formData.type} onChange={handleChange}>
                <MenuItem value="Essence">Essence</MenuItem>
                <MenuItem value="Diesel">Diesel</MenuItem>
              </Select>
              {errors.type && <FormHelperText error>{errors.type}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Type de moteur</Typography>
            <FormControl fullWidth sx={inputStyle} error={!!errors.typeMoteur}>
              <Select name="typeMoteur" value={formData.typeMoteur} onChange={handleChange}>
                <MenuItem value="Essence">Essence</MenuItem>
                <MenuItem value="Electrique">Electrique</MenuItem>
              </Select>
              {errors.typeMoteur && <FormHelperText error>{errors.typeMoteur}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Etat physique du véhicule</Typography>
            <FormControl fullWidth sx={inputStyle} error={!!errors.etatVehicule}>
              <Select name="etatVehicule" value={formData.etatVehicule} onChange={handleChange}>
                <MenuItem value="Neuf">Neuf</MenuItem>
                <MenuItem value="Occasion">Occasion</MenuItem>
              </Select>
              {errors.etatVehicule && <FormHelperText error>{errors.etatVehicule}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Date de mise en circulation</Typography>
            <TextField 
              fullWidth 
              type="date" 
              name="dateMiseEnCirculation" 
              value={formData.dateMiseEnCirculation} 
              onChange={handleChange} 
              variant="outlined" 
              sx={inputStyle} 
              error={!!errors.dateMiseEnCirculation}
              helperText={errors.dateMiseEnCirculation}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Kilométrage de départ</Typography>
            <TextField 
              fullWidth 
              type="number" 
              name="kmDepart" 
              value={formData.kmDepart} 
              onChange={handleChange} 
              variant="outlined" 
              sx={inputStyle} 
              error={!!errors.kmDepart}
              helperText={errors.kmDepart}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Plaque d'immatriculation</Typography>
            <TextField 
              fullWidth 
              name="plaqueImmatriculation" 
              value={formData.plaqueImmatriculation} 
              onChange={handleChange} 
              variant="outlined" 
              sx={inputStyle} 
              error={!!errors.plaqueImmatriculation}
              helperText={errors.plaqueImmatriculation}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Date d'acquisition</Typography>
            <TextField 
              fullWidth 
              type="date" 
              name="dateAcquisition" 
              value={formData.dateAcquisition} 
              onChange={handleChange} 
              variant="outlined" 
              sx={inputStyle} 
              error={!!errors.dateAcquisition}
              helperText={errors.dateAcquisition}
            />
          </Grid>

          <Grid item xs={6}>
              <FormControlLabel 
                mb={2} 
                control={
                  <Checkbox 
                    name="enService" 
                    checked={formData.enService} 
                    onChange={handleChange} 
                    sx={{'&.MuiCheckbox-indeterminate': { color: '#000' },'&.Mui-checked': { color: '#000' },}} 
                  />
                } 
                label="En service?" 
              />
              {errors.enService && <FormHelperText error>{errors.enService}</FormHelperText>}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">Chauffeur</Typography>
            <FormControl fullWidth sx={inputStyle} error={!!errors.chauffeur}>
              <Select 
                name="chauffeur" 
                value={formData.chauffeur} 
                onChange={handleChange}
                disabled={loading.chauffeurs}
              >
                {loading.chauffeurs ? (
                  <MenuItem value="">Chargement...</MenuItem>
                ) : error.chauffeurs ? (
                  <MenuItem value="">Erreur de chargement</MenuItem>
                ) : (
                  chauffeurs.map((chauffeur) => (
                    <MenuItem key={chauffeur.ID_User} value={chauffeur.ID_User}>
                      {chauffeur.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.chauffeur && <FormHelperText error>{errors.chauffeur}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
          <Box sx={{ backgroundColor: '#000', p: 1, mb: 1 }}>
          <Typography variant="subtitle1" color="white">
           PHOTOS
          </Typography>
        </Box>
            <Box
              sx={{
                width: 200,
                height: 150,
                border: errors.logo ? "1px solid red" : "1px solid #ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 2,
                cursor: "pointer",
                backgroundColor: "#f9f9f9",
              }}
              component="label"
            >
              {formData.logoPreview && !imageLoadError ? (
                <img 
                  src={formData.logoPreview} 
                  alt="Logo Preview" 
                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 2 }}
                  // Ajouter un gestionnaire d'erreur pour afficher l'icône si l'image ne peut pas être chargée
                  onError={() => {
                    console.error('Erreur de chargement de l\'image');
                    setImageLoadError(true);
                  }}
                />
              ) : (
                <AddIcon fontSize="large" sx={{ height: "150px", fontSize: "120px" }} color="action" />
              )}
              <input type="file" accept="image/*" hidden onChange={handleLogoChange} />
            </Box>
            {errors.logo && <FormHelperText error>{errors.logo}</FormHelperText>}
          </Grid>
        </Grid>
      </DialogContent>
      <Divider sx={{ mb: 1 }} />
      <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button variant="outlined" sx={{ color: "red", borderColor: "red", textTransform: "none", "&:hover": { backgroundColor: "red", color: "#fff", borderColor: "red" } }} startIcon={<CloseIcon />} onClick={handleClose}>
          Close
        </Button>
        <Button 
          variant="outlined" 
          sx={{ color: "red", borderColor: "red", textTransform: "none", "&:hover": { backgroundColor: "red", color: "#fff", borderColor: "red" } }} 
          startIcon={<CheckIcon />}
          onClick={handleSubmit}
          disabled={submitting || loading.vehicle}
        >
          {submitting ? 'Enregistrement...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
